import { useState } from 'react';
import { FolderIcon } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { BaseModal } from '@/shared/ui/BaseModal.jsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label';
import { usePostRequest } from '@/shared/hooks/requests.js';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { MAX_FOLDER_NAME_LENGTH, folderNameProblem, nameProblemMessage } from '@/shared/utils/filenames.js';
import { NODE_FOLDER_CREATE } from '@/shared/utils/urls.js';
import { toast } from 'sonner';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { useTranslation } from 'react-i18next';

// Server refusals that belong to the name field.
const NAME_CODES = { invalid_folder_name: 'name', folder_name_exists: 'name' };

export const Create = ({
  setData,
  // The folder the new one goes into (its uuid); null: the root.
  parentFolderUuid = null,
  // Optional controlled mode: lets a parent (e.g. the "+ New" menu) own the
  // dialog state and hide the built-in trigger button.
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;
  const createFolder = usePostRequest({ url: NODE_FOLDER_CREATE });
  // The backend's rule (app/core/filenames.py), checked as the person types.
  const problem = folderNameProblem(name);
  const checks = useFieldErrors(problem ? { name: nameProblemMessage(t, problem, 'folder') } : {});
  const overrides = {
    invalid_folder_name: (parsed) => nameProblemMessage(t, parsed.params?.reason || 'invalid_chars', 'folder'),
  };

  const changeOpen = (next) => {
    if (!next) {
      setName('');
      checks.reset();
    }
    setOpen(next);
  };

  const handleCreate = async (e) => {
    if (e) e.preventDefault();
    if (createFolder.loading || !checks.attempt()) return;

    const payload = { name: name.trim() };
    // Inside a folder: `parent_uuid` (the shared client decamelizes it).
    if (parentFolderUuid) {
      payload.parentUuid = parentFolderUuid;
    }

    const res = await createFolder.request({ data: payload, showError: false });
    if (res.success) {
      toast.success(t('my-docs:folder.create.success', 'Folder created successfully'));
      changeOpen(false);
      setData();
    } else {
      checks.showServer(res, ['name'], NAME_CODES, overrides);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          onClick={() => setOpen(true)}
          variant="ghost"
          size="sm"
          className="bg-[#F2F2F2] hover:bg-[#E5E5E5] text-black dark:bg-muted dark:text-foreground dark:hover:text-primary border-none shadow-none rounded-[4px] flex items-center justify-center gap-1.5 px-4 h-9"
        >
          <FolderIcon className="w-4 h-4" />
          {t('mainPage:actions.addFolder')}
        </Button>
      )}

      <BaseModal
        open={open}
        onOpenChange={changeOpen}
        title={t('my-docs:create.folder.title')}
      >
        <form className="space-y-3" onSubmit={handleCreate} noValidate>
          <div className="space-y-1">
            <Label htmlFor="folder-create-name" className={REQUIRED_LABEL}>
              {t('my-docs:create.folder.input')}
            </Label>
            <Input
              id="folder-create-name"
              data-testid="folder-name"
              placeholder={t('my-docs:create.folder.input')}
              value={name}
              required
              maxLength={MAX_FOLDER_NAME_LENGTH}
              onChange={(e) => { checks.touch('name'); setName(e.target.value); }}
              autoFocus
              {...checks.describe('name', 'folder-create')}
            />
            <FieldError id="folder-create-name-error" message={checks.errors.name} />
          </div>

          <div className="flex items-center gap-[10px] justify-end">
            <Button type="button" variant="ghost" onClick={() => changeOpen(false)}>
              {t('my-docs:create.folder.btn.cancel')}
            </Button>
            <Button
              type="submit"
              data-testid="folder-create-submit"
              disabled={createFolder.loading || !name.trim()}
              className="text-white font-medium bg-brand hover:bg-[#324E8C]"
            >
              {createFolder.loading ? <LoadingSpinner color="#fff" /> : t('my-docs:create.folder.btn.submit')}
            </Button>
          </div>
        </form>
      </BaseModal>
    </>
  );
};
